import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '../ui/Dialog';
import { Button } from '../ui/Button';
import { useProjectsStore } from '../../stores/projects-store';
import { useEditorStore } from '../../stores/editor-store';
import { useGenerationStore } from '../../stores/generation-store';
import { getPageSnapshots } from '../../utils/grapes-page-snapshots';
import { useLanguageStore } from '../../stores/language-store';
import { t } from '../../i18n/translations';

type VersionMeta = { id: string; comment?: string };

function getCurrentVersionId(currentProject: any): string {
  const meta: VersionMeta[] = currentProject?.contentVersionMeta ?? [];
  return currentProject?.activeContentVersionId ?? meta[meta.length - 1]?.id ?? '';
}

export function ReferenceDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const language = useLanguageStore((s) => s.language);
  const currentProject = useProjectsStore((s) => s.currentProject) as any;
  const loadProjectContent = useProjectsStore((s) => s.loadProjectContent);
  const editor = useEditorStore((s) => s.editorInstance) as any;
  const existingRef = useGenerationStore((s) => s.reference);
  const setReference = useGenerationStore((s) => s.setReference);

  const meta: VersionMeta[] = currentProject?.contentVersionMeta ?? [];
  const currentVersionId = getCurrentVersionId(currentProject);

  const [versionId, setVersionId] = useState<string>(currentVersionId);
  const [pageId, setPageId] = useState<string>('');
  const [loadingPages, setLoadingPages] = useState(false);
  const [pages, setPages] = useState<Array<{ id: string; name: string }>>([]);
  const [preferredPageName, setPreferredPageName] = useState<string>('');

  useEffect(() => {
    if (!open) return;
    const nextVersion = existingRef?.versionId ?? currentVersionId;
    setVersionId(nextVersion);
    setPageId(existingRef?.pageId ?? '');

    try {
      const selected = editor?.Pages?.getSelected?.();
      const selectedName = String(selected?.get?.('name') ?? '');
      if (selectedName) setPreferredPageName(selectedName);
      const selectedId = String(selected?.get?.('id') ?? '');
      if (!existingRef?.pageId && selectedId) setPageId(selectedId);
    } catch {
      // ignore
    }
  }, [open]);

  const versionLabel = useMemo(() => {
    const idx = meta.findIndex((m) => m.id === versionId);
    return idx >= 0 ? String(idx + 1) : 'Current';
  }, [meta, versionId]);

  useEffect(() => {
    if (!open || !currentProject) return;
    if (!versionId) return;

    const load = async () => {
      setLoadingPages(true);
      try {
        if (editor && versionId === currentVersionId) {
          const snaps = getPageSnapshots(editor);
          setPages(snaps.map((s) => ({ id: s.id, name: s.name })));
          return;
        }

        const content = await loadProjectContent(currentProject.id, { versionId });
        const snaps = (content as any)?.pageSnapshots as Array<{ id: string; name: string }> | undefined;
        if (Array.isArray(snaps) && snaps.length > 0) {
          setPages(snaps.map((s) => ({ id: String(s.id), name: String(s.name) })));
        } else {
          setPages([]);
        }
      } finally {
        setLoadingPages(false);
      }
    };

    void load();
  }, [open, versionId, currentProject?.id, editor, currentVersionId, loadProjectContent]);

  useEffect(() => {
    if (!open) return;
    if (!pages.length) return;
    if (pageId && pages.some((p) => p.id === pageId)) return;
    if (preferredPageName) {
      const byName = pages.find((p) => p.name === preferredPageName);
      if (byName) {
        setPageId(byName.id);
        return;
      }
    }
    if (!pageId) setPageId(pages[0]!.id);
  }, [open, pages, pageId, preferredPageName]);

  return (
    <Dialog open={open} onClose={onClose} title={t(language, 'reference')}>
      <div className="space-y-4">
        <div className="text-xs text-text-muted">{t(language, 'referenceHelp')}</div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-text-muted">{t(language, 'version')}</label>
          <select
            className="w-full bg-surface border border-surface-lighter rounded px-2 py-2 text-xs text-text focus:outline-none focus:border-accent"
            value={versionId}
            onChange={(e) => setVersionId(e.target.value)}
          >
            {meta.map((m, idx) => (
              <option key={m.id} value={m.id}>
                {idx + 1}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label className="text-xs font-semibold text-text-muted">{t(language, 'page')}</label>
          <select
            className="w-full bg-surface border border-surface-lighter rounded px-2 py-2 text-xs text-text focus:outline-none focus:border-accent"
            value={pageId}
            onChange={(e) => setPageId(e.target.value)}
            disabled={loadingPages || pages.length === 0}
          >
            <option value="">
              {loadingPages
                ? t(language, 'loading')
                : pages.length === 0
                  ? t(language, 'noPagesAvailable')
                  : t(language, 'selectPage')}
            </option>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {pages.length === 0 && !loadingPages && (
            <div className="text-[11px] text-text-muted">
              {language === 'ru'
                ? 'Если вы только включили эту функцию, сохраните проект один раз, чтобы появились снапшоты страниц для старых версий.'
                : 'If you just enabled this feature, save the project once to generate page snapshots for older versions.'}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2">
          <Button
            variant="secondary"
            onClick={() => {
              setReference(null);
              onClose();
            }}
          >
            {t(language, 'referenceClear')}
          </Button>
          <div className="flex items-center gap-2">
            <div className="text-[11px] text-text-muted">Selected: v{versionLabel}</div>
            <Button
              onClick={() => {
                if (!versionId || !pageId) return;
                setReference({ versionId, pageId });
                onClose();
              }}
              disabled={!versionId || !pageId}
            >
              {t(language, 'referenceApply')}
            </Button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
